import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { coreApi } from '../utils/coreApi';

/**
 * InterviewGenerationContext — Global state for background interview question generation.
 *
 * Tracks active generation across pages. Polls the progress endpoint for real-time
 * log updates. Persists across page navigation (state resets on hard refresh, but
 * CandidateDetail will auto-detect 'generating' status from DB on re-mount).
 */
const InterviewGenerationContext = createContext(null);

export const useInterviewGeneration = () => {
  const ctx = useContext(InterviewGenerationContext);
  if (!ctx) throw new Error('useInterviewGeneration must be used within InterviewGenerationProvider');
  return ctx;
};

export const InterviewGenerationProvider = ({ children }) => {
  const [generating, setGenerating] = useState(false);
  const [interviewId, setInterviewId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [phase, setPhase] = useState(''); // 'generating' | 'complete' | 'error' | ''
  const [candidateInfo, setCandidateInfo] = useState(null); // { name, jobTitle } for display
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Start tracking a generation
  const startGenerating = useCallback((id, info = {}) => {
    setInterviewId(id);
    setGenerating(true);
    setLogs(['Starting interview question generation...']);
    setPhase('generating');
    setCandidateInfo(info);
  }, []);

  // Stop tracking
  const stopGenerating = useCallback(() => {
    setGenerating(false);
    setInterviewId(null);
    // Keep logs briefly for the complete/error display, then clear
    setTimeout(() => {
      if (mountedRef.current) {
        setLogs([]);
        setPhase('');
        setCandidateInfo(null);
      }
    }, 8000);
  }, []);

  // Poll progress when generating
  useEffect(() => {
    if (!generating || !interviewId) return;

    let pollCount = 0;
    const maxPolls = 100; // ~5 min at 3s intervals

    const pollInterval = setInterval(async () => {
      pollCount++;

      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
        if (mountedRef.current) {
          setLogs(prev => [...prev, '⏱️ Generation timed out. Please try again.']);
          setPhase('error');
          setGenerating(false);
        }
        return;
      }

      try {
        const response = await coreApi.get(`/interview/${interviewId}/progress`);
        const data = response.data || response;

        if (!mountedRef.current) return;

        if (data?.logs?.length > 0) {
          setLogs(data.logs);
        }

        // Check if generation completed or failed
        if (data?.status === 'draft' || data?.status === 'completed') {
          clearInterval(pollInterval);
          setPhase('complete');
          setGenerating(false);
        } else if (data?.status === 'failed') {
          clearInterval(pollInterval);
          setPhase('error');
          setGenerating(false);
        }
      } catch (err) {
        // Keep polling on error
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [generating, interviewId]);

  const value = {
    generating,
    interviewId,
    logs,
    phase,
    candidateInfo,
    startGenerating,
    stopGenerating,
  };

  return (
    <InterviewGenerationContext.Provider value={value}>
      {children}
    </InterviewGenerationContext.Provider>
  );
};
